import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import { getHostMode } from "@/lib/hostMode";
import Login from "@/pages/Login";
import Landing from "@/pages/Landing";
import AppShell from "@/components/AppShell";
import Dashboard from "@/pages/Dashboard";
import Kunder from "@/pages/Kunder";
import KundeDetalj from "@/pages/KundeDetalj";
import NyKunde from "@/pages/NyKunde";
import Malepunkter from "@/pages/Malepunkter";
import Avtaler from "@/pages/Avtaler";
import Fakturering from "@/pages/Fakturering";
import Faktura from "@/pages/Faktura";
import Betaling from "@/pages/Betaling";
import Saker from "@/pages/Saker";
import Rapporter from "@/pages/Rapporter";
import Automatisering from "@/pages/Automatisering";
import Innstillinger from "@/pages/Innstillinger";
import Inkasso from "@/pages/Inkasso";
import Aktiver from "@/pages/Aktiver";
import Status from "@/pages/Status";
import Drift from "@/pages/Drift";
import Onboarding from "@/pages/Onboarding";
import ElhubData from "@/pages/ElhubData";
import ElhubMT1 from "@/pages/ElhubMT1";
import SuperadminTenants from "@/pages/superadmin/Tenants";
import PortalShell from "@/pages/portal/PortalShell";
import PortalDashboard from "@/pages/portal/PortalDashboard";
import { PortalFakturaer, PortalFaktura } from "@/pages/portal/PortalFakturaer";
import PortalForbruk from "@/pages/portal/PortalForbruk";
import PortalSaker from "@/pages/portal/PortalSaker";

function RootRoute() {
  const { user, loading } = useAuth();
  const mode = getHostMode();
  if (loading) return <div className="p-8 text-zinc-400">Laster…</div>;

  // krafthub.no → alltid landingsside (uavhengig av auth)
  if (mode === "landing") return <Landing />;

  // app.krafthub.no → hopp direkte til login/dashboard, aldri landingsside
  if (mode === "app") return user ? <AppShell /> : <Navigate to="/login" replace />;

  // dev/preview → som før: landing for uinnloggede, dashboard for innloggede
  return user ? <AppShell /> : <Landing />;
}

function Protected({ children, portal }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-zinc-400">Laster…</div>;
  if (!user) return <Navigate to="/login" replace />;
  // Bedriftsbruker → alltid portal
  if (user.role === "bedriftsbruker" && !portal) return <Navigate to="/portal" replace />;
  if (user.role !== "bedriftsbruker" && portal) return <Navigate to="/" replace />;
  return children;
}

function LandingOnlyRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/velkommen" element={<Landing />} />
      {/* app-ruter viderefører til app.krafthub.no */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  const mode = getHostMode();
  if (mode === "landing") {
    return (
      <div className="App min-h-screen bg-background text-foreground">
        <BrowserRouter>
          <LandingOnlyRoutes />
        </BrowserRouter>
        <Toaster theme="dark" richColors position="top-right" />
      </div>
    );
  }
  return (
    <div className="App min-h-screen bg-background text-foreground">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/velkommen" element={<Landing />} />
            <Route path="/aktiver" element={<Aktiver />} />
            <Route
              path="/"
              element={<RootRoute />}
            >
              <Route index element={<Dashboard />} />
              <Route path="kunder" element={<Kunder />} />
              <Route path="kunder/ny" element={<NyKunde />} />
              <Route path="kunder/:id" element={<KundeDetalj />} />
              <Route path="malepunkter" element={<Malepunkter />} />
              <Route path="avtaler" element={<Avtaler />} />
              <Route path="fakturering" element={<Fakturering />} />
              <Route path="fakturering/:id" element={<Faktura />} />
              <Route path="betaling" element={<Betaling />} />
              <Route path="inkasso" element={<Inkasso />} />
              <Route path="saker" element={<Saker />} />
              <Route path="rapporter" element={<Rapporter />} />
              <Route path="automatisering" element={<Automatisering />} />
              <Route path="drift" element={<Drift />} />
              <Route path="status" element={<Status />} />
              <Route path="onboarding" element={<Onboarding />} />
              <Route path="elhub-data" element={<ElhubData />} />
              <Route path="elhub-mt1" element={<ElhubMT1 />} />
              <Route path="superadmin/tenants" element={<SuperadminTenants />} />
              <Route path="innstillinger" element={<Innstillinger />} />
            </Route>

            <Route path="/portal" element={<Protected portal><PortalShell /></Protected>}>
              <Route index element={<PortalDashboard />} />
              <Route path="forbruk" element={<PortalForbruk />} />
              <Route path="fakturaer" element={<PortalFakturaer />} />
              <Route path="fakturaer/:id" element={<PortalFaktura />} />
              <Route path="saker" element={<PortalSaker />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster theme="dark" richColors position="top-right" />
      </AuthProvider>
    </div>
  );
}

export default App;
